/**
 * Contracts for module "growth-graph". Owner: growth-os. Mounted at /v1/growth-graph (flag growth_os.graph).
 * Tenant-scoped graph of the things growth work is about (business, channels, keywords, competitors, audiences,
 * assets, campaigns, leads, results) and the edges between them. Fed read-only from audit, CRM, WhatsApp and
 * visibility; consumed by command-center, campaign-os and autonomy. See docs/specs/growth-os.md.
 */
import { z } from 'zod';
import { IsoDateTimeSchema } from './common';

export const GRAPH_FLAG = 'growth_os.graph';
export const GRAPH_SYNC_FLAG = 'growth_os.graph_sync';

export const NodeTypeSchema = z.enum(['business', 'channel', 'keyword', 'competitor', 'audience', 'asset', 'campaign', 'lead', 'result']);
export type NodeType = z.infer<typeof NodeTypeSchema>;

export const EdgeTypeSchema = z.enum([
  'operates', // business -> channel
  'competes_with', // business -> competitor
  'targets', // business|campaign -> keyword|audience
  'ranks_for', // business|competitor -> keyword
  'runs_on', // campaign|asset -> channel
  'uses', // campaign -> asset
  'sourced_from', // lead -> channel
  'attributed_to', // lead -> campaign
  'reaches', // channel -> audience
  'measured_by', // business|channel|campaign|asset|keyword -> result
]);
export type EdgeType = z.infer<typeof EdgeTypeSchema>;

/** Allowed (from type -> to types) per edge type. The API refuses any other combination. */
export const EDGE_RULES: Record<EdgeType, { from: readonly NodeType[]; to: readonly NodeType[] }> = {
  operates: { from: ['business'], to: ['channel'] },
  competes_with: { from: ['business'], to: ['competitor'] },
  targets: { from: ['business', 'campaign'], to: ['keyword', 'audience'] },
  ranks_for: { from: ['business', 'competitor'], to: ['keyword'] },
  runs_on: { from: ['campaign', 'asset'], to: ['channel'] },
  uses: { from: ['campaign'], to: ['asset'] },
  sourced_from: { from: ['lead'], to: ['channel'] },
  attributed_to: { from: ['lead'], to: ['campaign'] },
  reaches: { from: ['channel'], to: ['audience'] },
  measured_by: { from: ['business', 'channel', 'campaign', 'asset', 'keyword'], to: ['result'] },
};
export const isValidEdge = (from: NodeType, type: EdgeType, to: NodeType): boolean =>
  EDGE_RULES[type].from.includes(from) && EDGE_RULES[type].to.includes(to);

/** Where a node/edge came from. `manual` = entered by a tenant user; the rest are read-only projections. */
export const GraphSourceSchema = z.enum(['manual', 'audit', 'crm', 'whatsapp', 'visibility', 'campaign_os']);
export type GraphSource = z.infer<typeof GraphSourceSchema>;

const KeySchema = z.string().trim().min(1).max(160);
/** Attributes are small, flat and PII-free: no names, emails or phone numbers (lead nodes carry status/score only). */
export const AttrsSchema = z.record(z.string().max(60), z.union([z.string().max(300), z.number(), z.boolean(), z.null()])).refine((o) => Object.keys(o).length <= 30, 'max 30 attributes');
export type Attrs = z.infer<typeof AttrsSchema>;

export const UpsertNodeSchema = z.object({
  type: NodeTypeSchema,
  /** natural key, unique per (tenant, type): a domain, a keyword text, a source record id */
  key: KeySchema,
  label: z.string().trim().min(1).max(200),
  attrs: AttrsSchema.default({}),
});
export type UpsertNodeInput = z.infer<typeof UpsertNodeSchema>;

export const NodeRefSchema = z.object({ type: NodeTypeSchema, key: KeySchema });
export type NodeRef = z.infer<typeof NodeRefSchema>;

export const UpsertEdgeSchema = z.object({
  from: NodeRefSchema,
  to: NodeRefSchema,
  type: EdgeTypeSchema,
  /** 0..1 strength/confidence */
  weight: z.number().min(0).max(1).default(1),
}).refine((e) => isValidEdge(e.from.type, e.type, e.to.type), { message: 'edge type not allowed between these node types', path: ['type'] });
export type UpsertEdgeInput = z.infer<typeof UpsertEdgeSchema>;

export const IngestSchema = z.object({
  nodes: z.array(UpsertNodeSchema).max(200).default([]),
  edges: z.array(UpsertEdgeSchema).max(500).default([]),
});
export type IngestInput = z.infer<typeof IngestSchema>;

export const GraphNodeSchema = z.object({
  id: z.string(), type: NodeTypeSchema, key: z.string(), label: z.string(), attrs: AttrsSchema,
  source: GraphSourceSchema, firstSeenAt: IsoDateTimeSchema, lastSeenAt: IsoDateTimeSchema,
});
export type GraphNode = z.infer<typeof GraphNodeSchema>;

export const GraphEdgeSchema = z.object({
  id: z.string(), type: EdgeTypeSchema, fromId: z.string(), toId: z.string(), weight: z.number(),
  source: GraphSourceSchema, lastSeenAt: IsoDateTimeSchema,
});
export type GraphEdge = z.infer<typeof GraphEdgeSchema>;

export const NodeListQuerySchema = z.object({
  type: NodeTypeSchema.optional(),
  q: z.string().trim().max(100).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const NeighborsQuerySchema = z.object({
  depth: z.coerce.number().int().min(1).max(2).default(1),
  edgeType: EdgeTypeSchema.optional(),
});

export const SyncRequestSchema = z.object({
  sources: z.array(z.enum(['audit', 'crm', 'whatsapp', 'visibility'])).min(1).max(4).default(['audit', 'crm', 'whatsapp', 'visibility']),
});
export type SyncRequest = z.infer<typeof SyncRequestSchema>;

export const SyncSourceResultSchema = z.object({
  source: z.enum(['audit', 'crm', 'whatsapp', 'visibility']),
  status: z.enum(['ok', 'skipped', 'failed']),
  nodes: z.number().int(), edges: z.number().int(),
  /** short machine reason when skipped/failed, never a provider/SQL error body */
  reason: z.string().nullable(),
});
export const SyncRunSchema = z.object({
  id: z.string(), startedAt: IsoDateTimeSchema, finishedAt: IsoDateTimeSchema, results: z.array(SyncSourceResultSchema),
});
export type SyncRun = z.infer<typeof SyncRunSchema>;

export const GraphSummarySchema = z.object({
  nodesByType: z.record(NodeTypeSchema, z.number().int()),
  edgeCount: z.number().int(),
  lastSync: SyncRunSchema.nullable(),
  /** ISO time of the newest node, null when the graph is empty */
  freshestAt: IsoDateTimeSchema.nullable(),
});
export type GraphSummary = z.infer<typeof GraphSummarySchema>;
